import Toggle from "../../../components/Toggle";
import React, {useEffect, useRef, useState} from "react";
import {PlayerLite} from "../interfaces";
import {useSocket} from "../SocketContext";
import {BodyCell, BodyRow, BodyRowMapper, HeaderCell, HeaderRow, Table} from "../../../components/Table";
import OverlayBase from "../overlay/OverlayBase";
import Button from "../../../components/Button";
import {rpc_updateWhitelistState, rpc_updateWhitelistPlayer, rpc_updateWhitelistReason} from "../rpc";
import {useOverlay} from "../overlay/OverlayContext";
import {useWhitelist} from "./WhitelistContext";
import {LucideAlertCircle, LucidePlus, LucideSave, LucideUnplug} from "lucide-react";
import {useWatcher} from "../../../hooks/useWatcher";

export default function Whitelist(){

    const socket = useSocket()
    const overlay = useOverlay()
    const whitelist = useWhitelist()

    const [rejectMessage, setRejectMessage] = useState(whitelist.data.rejectMessage)

    useWatcher(whitelist.data.rejectMessage, (current, prev) => {
        setRejectMessage(current)
    })

    useWatcher(socket.isConnected, (current, prev) => {
        whitelist.reload()
    })

    useEffect(() => {
        if (whitelist.data.error){
            whitelist.reload()
        }
    })

    const setEnabled = async (enabled: boolean) => {
        const result = await rpc_updateWhitelistState(socket, enabled)

        if (!result.success) {
            overlay.showAlert("Failed update whitelist state", `Error: ${result.message ?? "Unknown error (-1)"}`)
        }
        else{
            whitelist.reload()
        }
    }

    const updateReason = async () => {
        const result = await rpc_updateWhitelistReason(socket, rejectMessage ?? "")

        if (!result.success) {
            overlay.showAlert("Failed update whitelist message", `Error: ${result.message ?? "Unknown error (-1)"}`)
            setRejectMessage(whitelist.data.rejectMessage)
        }
    }

    const [showAddOverlay, setShowAddOverlay] = useState<boolean>(false)

    const isLoading = !whitelist.data.error && whitelist.data.players === undefined;
    const isError = !whitelist.data.players && whitelist.data.error;

    if (isError && whitelist.data.error === "not_connected") {
        return <div className="flex flex-col items-center justify-center h-full p-4 text-center space-y-2">
            <LucideUnplug className="text-red-500" size={40} />
            <p className="text-red-500 text-xl" >Server not connected</p>
            <p>Whitelist is only available when the server is connected</p>
        </div>
    }

    if (isError && (whitelist.data.error === "data_error" || whitelist.data.error === "parse_error")) {
        return <div className="flex flex-col items-center justify-center h-full p-4 text-center space-y-2">
            <LucideAlertCircle className="text-red-500" size={40} />
            <p className="text-red-500 text-xl" >Invalid data</p>
            <p>Make sure the latest version of the <a className="text-primary hover:underline" rel="noreferrer" href="https://codefling.com/plugins/rust-server-tool" target="_blank">RustServerTool plugin</a> is installed on your server.</p>
            <p> This error can also happen when your server is still starting.</p>
        </div>
    }

    return (
        <div className="size-full flex flex-col p-4 relative gap-2">

            {showAddOverlay && <AddPlayerOverlay hide={() => setShowAddOverlay(false)} />}

            {isLoading && <p>Loading ...</p>}
            {isError && <p className="text-red-500">Error: {whitelist.data.error}</p>}
            {whitelist.data.players && (<>


                <h2 className="text-xl font-semibold">Whitelist</h2>
                <div className="mb-4 border border-neutral-500 rounded-md p-4" >
                    <Toggle label="Enable Whitelist" checked={whitelist.data.enabled} onChange={(e) => setEnabled(e.target.checked)}/>

                    <div>
                        <label htmlFor="reason">Message:</label>
                        <div className="flex flex-row">
                            <input id="reason" value={rejectMessage ?? ""} onChange={e => setRejectMessage(e.target.value)}
                                   className="rst-input w-full"
                                   placeholder="Message for non-whitelist players" type="text"/>
                            <Button text="Save" className="ms-2 bg-primary hover:bg-primary-hover" onClick={updateReason} ><LucideSave size={16} />&nbsp;Save</Button>
                        </div>
                    </div>
                </div>

                <div className="flex flex-row justify-between items-baseline">
                    <h2 className="text-xl font-semibold">Whitelisted Players</h2>
                    <Button text="Add Player" className="bg-green-600" onClick={() => setShowAddOverlay(true)} ><LucidePlus size={18} className="me-2" /> Add Player</Button>
                </div>

                <Table>
                    <HeaderRow>
                        <HeaderCell sortKey="steamId">Steam ID</HeaderCell>
                        <HeaderCell>Display Name</HeaderCell>
                        <HeaderCell></HeaderCell>
                    </HeaderRow>
                    <BodyRowMapper placeholder="No players on the whitelist" data={whitelist.data.players} map={p => (
                        <WhitelistRow key={p.steamId} player={p}/>
                    )}/>
                </Table>
            </>)}

        </div>
    )

    function WhitelistRow(props: {player: PlayerLite}){

        const overlay = useOverlay()
        const [loading, setLoading] = useState<boolean>(false)

        const removePlayer = async () => {

            setLoading(true)

            const result = await rpc_updateWhitelistPlayer(socket, props.player.steamId, true)

            setLoading(false)

            if (!result.success) {
                overlay.showAlert("Failed to remove player", `Error: ${result.message ?? "Unknown error (-1)"}`)
            }
        }

        return <BodyRow key={props.player.steamId} highlight={false}>
            <BodyCell>{props.player.steamId}</BodyCell>
            <BodyCell>{props.player.displayName}</BodyCell>
            <BodyCell>
                <Button text="Remove" padding="none" className="bg-red-500 px-2" loading={loading} onClick={removePlayer} />
            </BodyCell>
        </BodyRow>
    }
}


function AddPlayerOverlay(props: { hide: () => void }){

    const [loading, setLoading] = useState<boolean>(false)
    const [steamId, setSteamId] = useState<string>("")

    const [error, setError] = useState<string>("")
    const [validationError, setValidationError] = useState<string>("")

    const socket = useSocket()

    const handleSubmit = async () => {
        if (loading){
            return
        }
        setError("")
        setValidationError("")
        if (!steamId || !steamId.trim()) {
            setValidationError("Invalid steam id")
            return
        }
        setLoading(true)

        const result = await rpc_updateWhitelistPlayer(socket, steamId, false)

        setLoading(false)

        if (result.success){
            props.hide()
        }
        else{
            let err = `Error: ${result.message ?? "Unknown error (-1)"}`
            if (result.message === "server_not_connected"){
                err = `The game server is not connected to RustServerTool (${err})`
            }
            else if (result.message === "server_timeout"){
                err = `The player is already whitelisted or the game server did not respond in time (${err})`
            }
            setError(err);
        }
    };

    return <OverlayBase hide={() => props.hide()} w="w-[28rem]">
        <h2 className="text-xl font-semibold mb-4">Add player to whitelist</h2>
        <div className="space-y-4">
            <div>
                <label className="block text-sm font-medium">Steam ID</label>
                <input
                    type="number"
                    value={steamId}
                    onChange={(e) => setSteamId(e.target.value)}
                    placeholder="Enter SteamID64"
                    className="w-full rst-input"
                />
                {validationError && <p className="text-red-500 text-sm mt-1">{validationError}</p>}
            </div>
        </div>
        <div className="mt-6 flex flex-col items-end">
            {error && <p className="text-red-500 text-sm mb-2 text-right">{error}</p>}
            <div className="flex justify-end gap-3">
                <button onClick={() => props.hide()}
                        className="px-4 py-2 hover:bg-neutral-700 border border-neutral-500 rounded-lg">Cancel
                </button>
                <Button text="Add" className="bg-green-600 hover:bg-green-700" onClick={handleSubmit} loading={loading} />
            </div>
        </div>
    </OverlayBase>

}